import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../api.service';
import {
  ExerciseResponse,
  CalendarResponse,
  PetitionResponse,
  DriverResponse,
  DriverProfileResponse,
  Operative,
  ExerciseOperativeAssignment,
  OperativeWeekConfigsResponse,
  SyncWeekConfigsResponse,
  SyncProcessStartResponse,
  SyncProcessStatus,
  DriverAssignmentResponse,
  OperativaStatsResponse,
  OperativeDriversResponse,
  DriverRequestsResponse,
  OperativeRequestsResponse,
  OperativaGroup,
  OperativaSyncResponse,
  AvailableResourcesResponse,
  GroupedOperativa,
  ExerciseOperativaGroupsResponse,
  ExerciseDriversResponse,
  ExerciseDriverRequestsResponse,
  OperativaGroupRequestsResponse,
  OperativaGroupDriversResponse,
  OperativaAssignmentDetails,
  WeekDriverDetailsRequest,
  WeekDriverDetailsResponse,
  OperativaGroupHoliday,
  OperativaGroupHolidayRequest,
  OperativaGroupOperativasResponse,
  UpsertOperativaAssignmentRequest,
  UpsertOperativaAssignmentResponse,
  ProtocolImportResponse,
  DownloadOperativaAssignmentsResponse,
  CreateHolidayRequestPayload,
  CreateHolidayRequestResponse,
} from '../../interfaces/holidays.interface';

@Injectable({
  providedIn: 'root',
})
export class HolidaysService {
  constructor(private apiService: ApiService) {}

  getExercises(mock: boolean = false): Observable<ExerciseResponse[]> {
    const path = mock
      ? '/holidays/exercises/?mock=true'
      : '/holidays/exercises/';
    return this.apiService.buildAPIGet<ExerciseResponse[]>(path);
  }

  postExercise(data: any): Observable<ExerciseResponse> {
    const path = '/holidays/exercises/';
    return this.apiService.buildAPIPost<ExerciseResponse>(path, data);
  }

  getExerciseById(
    id: number,
    mock: boolean = false
  ): Observable<ExerciseResponse> {
    const path = mock
      ? `/holidays/exercises/${id}/?mock=true`
      : `/holidays/exercises/${id}/?stats=true`;
    return this.apiService.buildAPIGet<ExerciseResponse>(path);
  }

  getOperatives(mock: boolean = false): Observable<any[]> {
    const path = mock
      ? '/holidays/operativas/?mock=true'
      : '/holidays/operativas/';
    return this.apiService.buildAPIGet<any[]>(path);
  }

  getGroups(params?: {
    active?: boolean;
    include_operativas?: boolean;
    mock?: boolean;
  }): Observable<OperativaGroup[]> {
    let path = params?.mock
      ? '/holidays/operativa-groups/?mock=true'
      : '/holidays/operativa-groups/';

    // Add query parameters if provided
    const queryParams: string[] = [];
    if (params?.mock) {
      queryParams.push('mock=true');
    }
    if (params?.active !== undefined) {
      queryParams.push(`active=${params.active}`);
    }
    if (params?.include_operativas !== undefined) {
      queryParams.push(`include_operativas=${params.include_operativas}`);
    }

    if (queryParams.length > 0) {
      path = `/holidays/operativa-groups/?${queryParams.join('&')}`;
    }

    return this.apiService.buildAPIGet<OperativaGroup[]>(path);
  }

  getGroupById(
    id: number,
    params?: {
      include_operativas?: boolean;
      mock?: boolean;
    }
  ): Observable<OperativaGroup> {
    const queryParams: string[] = [];
    if (params?.mock) {
      queryParams.push('mock=true');
    }
    if (params?.include_operativas !== undefined) {
      queryParams.push(`include_operativas=${params.include_operativas}`);
    }

    const queryString =
      queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    const path = `/holidays/operativa-groups/${id}/${queryString}`;
    return this.apiService.buildAPIGet<OperativaGroup>(path);
  }

  syncOperativas(token?: string): Observable<OperativaSyncResponse> {
    const path = '/holidays/operativas/sync/';
    const body = token ? { token } : {};
    return this.apiService.buildAPIPost<OperativaSyncResponse>(path, body);
  }

  getAvailableResources(data: {
    init: string; // ISO datetime
    end: string; // ISO datetime
    idGroupOperative: number; // Group ID (not operative ID!)
  }): Observable<AvailableResourcesResponse> {
    const path = '/holidays/available-resources/';
    return this.apiService.buildAPIPost<AvailableResourcesResponse>(path, data);
  }

  getProtocols(mock: boolean = false): Observable<any[]> {
    const path = mock
      ? '/holidays/protocols/?mock=true'
      : '/holidays/protocols/';
    return this.apiService.buildAPIGet<any[]>(path);
  }

  getProtocolById(id: number, mock: boolean = false): Observable<any> {
    const path = mock
      ? `/holidays/protocols/${id}/?mock=true`
      : `/holidays/protocols/${id}/`;
    return this.apiService.buildAPIGet<any>(path);
  }

  updateProtocol(id: number, data: any): Observable<any> {
    const path = `/holidays/protocols/`;
    return this.apiService.buildAPIPut<any>(path, id, data);
  }

  postProtocol(data: any): Observable<any> {
    const path = `/holidays/protocols/`;
    return this.apiService.buildAPIPost<any>(path, data);
  }

  importProtocol(data: any): Observable<ProtocolImportResponse> {
    const path = `/holidays/protocols/import/`;
    return this.apiService.buildAPIPost<ProtocolImportResponse>(path, data);
  }

  copyProtocol(id: number): Observable<any> {
    const path = `/holidays/protocols/${id}/copy/`;
    return this.apiService.buildAPIPost<any>(path, {});
  }

  getRuleGroups(): Observable<any[]> {
    const path = '/holidays/rule-groups/';
    return this.apiService.buildAPIGet<any[]>(path);
  }

  getRuleGroupsByProtocol(protocolId: number): Observable<any[]> {
    const path = `/holidays/rule-groups/?protocol=${protocolId}`;
    return this.apiService.buildAPIGet<any[]>(path);
  }

  getRuleGroupById(id: number): Observable<any> {
    const path = `/holidays/rule-groups/${id}/`;
    return this.apiService.buildAPIGet<any>(path);
  }

  updateRuleGroup(id: number, data: any): Observable<any> {
    const path = `/holidays/rule-groups/`;
    return this.apiService.buildAPIPut<any>(path, id, data);
  }

  getRules(): Observable<any[]> {
    const path = '/holidays/rules/';
    return this.apiService.buildAPIGet<any[]>(path);
  }

  getRuleById(id: number): Observable<any> {
    const path = `/holidays/rules/${id}/`;
    return this.apiService.buildAPIGet<any>(path);
  }

  updateRule(id: number, data: any): Observable<any> {
    const path = `/holidays/rules/`;
    return this.apiService.buildAPIPut<any>(path, id, data);
  }

  updateExercise(id: number, data: any): Observable<any> {
    const path = `/holidays/exercises/`;
    return this.apiService.buildAPIPut<any>(path, id, data);
  }

  getExerciseOperatives(id: number): Observable<ExerciseOperativeAssignment> {
    const path = `/holidays/exercises/${id}/operativa-groups/`;
    return this.apiService.buildAPIGet<ExerciseOperativeAssignment>(path);
  }

  getExerciseOperativaGroup(
    exerciseId: number,
    operativaGroupId: number
  ): Observable<ExerciseOperativaGroupsResponse> {
    const path = `/holidays/exercises/${exerciseId}/operativa-groups/?operativa_group_id=${operativaGroupId}`;
    return this.apiService.buildAPIGet<ExerciseOperativaGroupsResponse>(path);
  }

  postExerciseOperatives(data: any, id: number): Observable<any> {
    const path = `/holidays/operativa-assignments/`;
    return this.apiService.buildAPIPost<any>(path, data);
  }

  unassignExerciseOperative(id: number): Observable<any> {
    const path = `/holidays/operativa-assignments/${id}/unassign/`;
    return this.apiService.buildAPIPost<any>(path, {});
  }

  getOperativeById(id: number, mock: boolean = false): Observable<Operative> {
    const path = mock
      ? `/holidays/operativas/${id}/?mock=true`
      : `/holidays/operativas/${id}/`;
    return this.apiService.buildAPIGet<Operative>(path);
  }

  getOperativeCalendar(
    id: number,
    mock: boolean = false
  ): Observable<CalendarResponse> {
    const path = mock
      ? `/holidays/operativas/${id}/calendar/?mock=true`
      : `/holidays/operativas/${id}/calendar/`;
    return this.apiService.buildAPIGet<CalendarResponse>(path);
  }

  getOperativeRequests(data: {
    operative_id: number;
    exercise_id: number;
    filter: string;
  }): Observable<OperativeRequestsResponse> {
    return this.apiService.buildAPIPost<OperativeRequestsResponse>(
      '/holidays/operativas/requests/',
      data
    );
  }

  getOperativeDrivers(data: {
    operativa_id: number;
    exercise_id: number;
    driver_id?: number;
  }): Observable<OperativeDriversResponse> {
    return this.apiService.buildAPIPost<OperativeDriversResponse>(
      '/holidays/operativas/drivers/',
      data
    );
  }

  getOperativaGroupRequests(data: {
    operativa_group_id: number;
    exercise_id: number;
    filter: string;
  }): Observable<OperativaGroupRequestsResponse> {
    return this.apiService.buildAPIPost<OperativaGroupRequestsResponse>(
      '/holidays/operativa-groups/requests/',
      data
    );
  }

  getOperativaGroupDrivers(data: {
    operativa_group_id: number;
    exercise_id: number;
    driver_id?: number;
  }): Observable<OperativaGroupDriversResponse> {
    return this.apiService.buildAPIPost<OperativaGroupDriversResponse>(
      '/holidays/operativa-groups/drivers/',
      data
    );
  }

  getAllDrivers(exerciseId: number, data: {
    exercise_id: number;
    driver_id?: number;   
  }): Observable<ExerciseDriversResponse> {
      const path = `/holidays/exercises/${exerciseId}/drivers/`;
    return this.apiService.buildAPIPost<ExerciseDriversResponse>(path, data);
  }

  getExerciseDriversRequests(exerciseId: number, data: {
    exercise_id: number;
    filter: string;
  }): Observable<ExerciseDriverRequestsResponse> {
      const path = `/holidays/exercises/${exerciseId}/requests/`;
    return this.apiService.buildAPIPost<ExerciseDriverRequestsResponse>(path, data);
  }

  getDriverRequests(data: {
    driver_id: number;
    operativa_id: number;
    exercise_id: number;
  }): Observable<DriverRequestsResponse> {
    return this.apiService.buildAPIPost<DriverRequestsResponse>(
      '/holidays/drivers/vacation-requests/',
      data
    );
  }

  getDriverProfile(
    id: number,
    mock: boolean = false
  ): Observable<DriverProfileResponse> {
    const path = mock
      ? `/holidays/drivers/${id}/data/?mock=true`
      : `/holidays/drivers/${id}/data/`;
    return this.apiService.buildAPIGet<DriverProfileResponse>(path);
  }

  changeExerciseActiveStatus(id: number, isActive: boolean): Observable<any> {
    const path = `/holidays/exercises/`;
    const body = {
      active: isActive,
    };
    return this.apiService.buildAPIPut<any>(path, id, body);
  }

  getOperativeWeekConfigs(data: {
    operativa_group_id: number;
    exercise_id: number;
    year?: number;
    week_numbers?: number[];
  }): Observable<OperativeWeekConfigsResponse> {
    const path = `/holidays/operativa-week-configs/`;
    return this.apiService.buildAPIPost<OperativeWeekConfigsResponse>(
      path,
      data
    );
  }

  syncWeekConfigs(data: {
    operativa_group_id: number;
    exercise_id: number;
  }): Observable<SyncProcessStartResponse> {
    const path = `/holidays/operativa-assignments/sync-weeks/`;
    return this.apiService.buildAPIPost<SyncProcessStartResponse>(path, data);
  }

  getSyncProcessStatus(processId: string): Observable<SyncProcessStatus> {
    const path = `/holidays/sync-processes/${processId}/`;
    return this.apiService.buildAPIGet<SyncProcessStatus>(path);
  }

  refreshWeekConfig(data: {
    operativa_group_id: number;
    exercise_id: number;
    week_numbers: number[];
  }): Observable<SyncWeekConfigsResponse> {
    const path = `/holidays/operativa-assignments/sync-weeks/`;
    return this.apiService.buildAPIPost<SyncWeekConfigsResponse>(path, data);
  }

  updateWeekConfig(
    id: number,
    data: {
      camions_plaza?: number;
      camions_parats_objectiu?: number;
      camions_futur?: number;
      conductors_previstos?: number;
      slots?: number;
    }
  ): Observable<any> {
    const path = `/holidays/operativa-week-configs/`;
    return this.apiService.buildAPIPatch<any>(path, id, data);
  }

  getDriverAssignments(data: {
    operativa: number;
    exercise: number;
  }): Observable<DriverAssignmentResponse> {
    const path = `/holidays/driver-assignments/filter/`;
    return this.apiService.buildAPIPost<DriverAssignmentResponse>(path, data);
  }

  validateRules(data: {
    driver_assignment: number;
    dates: string[];
  }): Observable<any> {
    const path = `/holidays/vacation-requests/validate-rules/`;
    return this.apiService.buildAPIPost<any>(path, data);
  }

  validateSlots(data: {
    driver_assignment: number;
    dates: string[];
  }): Observable<any> {
    const path = `/holidays/vacation-requests/validate-slots/`;
    return this.apiService.buildAPIPost<any>(path, data);
  }

  rejectPetition(uuid: string, comment?: string): Observable<any> {
    const id = uuid + '/status';
    const path = `/holidays/vacation-requests/`;
    return this.apiService.buildAPIPatch<any>(path, id, {
      status: 'rejected',
      comment,
    });
  }

  acceptPetition(uuid: string): Observable<any> {
    const id = uuid + '/status';
    const path = `/holidays/vacation-requests/`;
    return this.apiService.buildAPIPatch<any>(path, id, { status: 'approved' });
  }

  getOperativaExerciseAssignment(
    operativaGroupId: number,
    exerciseId: number,
    status?: string
  ): Observable<OperativaAssignmentDetails[]> {
    let path = `/holidays/operativa-assignments/?operativa_group_id=${operativaGroupId}&exercise_id=${exerciseId}`;
    if (status) {
      path += `&status=${status}`;
    }
    return this.apiService.buildAPIGet<OperativaAssignmentDetails[]>(path);
  }

  updateOperativaProtocol(
    assignmentId: number,
    protocolId: number
  ): Observable<any> {
    const body = { protocol: protocolId };
    const basePath = `/holidays/operativa-assignments/`;
    return this.apiService.buildAPIPatch<any>(
      basePath,
      `${assignmentId}/update-protocol`,
      body
    );
  }

  updateOperativaAssignment(
    assignmentId: number,
    data: {
      protocol?: number;
      type_days?: string;
      holidays_number?: number | null;
      personal_days_number?: number | null;
    }
  ): Observable<any> {
    const basePath = `/holidays/operativa-assignments/`;
    return this.apiService.buildAPIPatch<any>(basePath, assignmentId, data);
  }

  getVacationPriorityPeriods(): Observable<any[]> {
    const path = '/holidays/vacation-priority-periods/';
    return this.apiService.buildAPIGet<any[]>(path);
  }

  getVacationPriorityPeriodById(id: number): Observable<any> {
    const path = `/holidays/vacation-priority-periods/${id}/`;
    return this.apiService.buildAPIGet<any>(path);
  }

  postVacationPriorityPeriod(data: any): Observable<any> {
    const path = `/holidays/vacation-priority-periods/`;
    return this.apiService.buildAPIPost<any>(path, data);
  }

  updateVacationPriorityPeriod(id: number, data: any): Observable<any> {
    const path = `/holidays/vacation-priority-periods/`;
    return this.apiService.buildAPIPut<any>(path, id, data);
  }

  deleteVacationPriorityPeriod(id: number): Observable<any> {
    const path = `/holidays/vacation-priority-periods/`;
    return this.apiService.buildAPIDelete(path, id);
  }

  getProtocolOperativaStats(): Observable<OperativaStatsResponse> {
    const path = '/holidays/protocols/operativa-stats/';
    return this.apiService.buildAPIGet<OperativaStatsResponse>(path);
  }

  deleteRule(id: number): Observable<any> {
    const path = `/holidays/rules/`;
    return this.apiService.buildAPIDelete(path, id);
  }

  // Utility method to group operativas by their parent group
  groupOperativasByGroup(operativas: Operative[]): GroupedOperativa[] {
    const grouped = new Map<number, GroupedOperativa>();

    operativas.forEach((op) => {
      if (op.group) {
        if (!grouped.has(op.group.id)) {
          grouped.set(op.group.id, {
            group: op.group,
            operativas: [],
          });
        }
        grouped.get(op.group.id)!.operativas.push(op);
      }
    });

    return Array.from(grouped.values());
  }

  getOperativaGroupDriver(data: {
    exercise_id: number;
    operativa_group_id: number;
    driver_id: number;
  }): Observable<OperativaGroupDriversResponse> {
    const path = `/holidays/operativa-groups/drivers/`;
    return this.apiService.buildAPIPost<OperativaGroupDriversResponse>(
      path,
      data
    );
  }

  getOperativaGroupDriverRequests(data: {
    exercise_id: number;
    operativa_group_id: number;
    driver_id: number;
  }): Observable<DriverRequestsResponse> {
    const path = `/holidays/operativa-groups/requests/`;
    return this.apiService.buildAPIPost<DriverRequestsResponse>(path, data);
  }

  getWeekDriverDetails(
    data: WeekDriverDetailsRequest
  ): Observable<WeekDriverDetailsResponse> {
    const path = `/holidays/statistics/week-driver-details/`;
    return this.apiService.buildAPIPost<WeekDriverDetailsResponse>(path, data);
  }

  syncOperatives(): Observable<any> {
    const path = '/holidays/operativas/sync/';
    return this.apiService.buildAPIPost<any>(path);
  }

  syncDrivers(): Observable<any> {
    const path = '/holidays/drivers/sync/';
    return this.apiService.buildAPIPost<any>(path);
  }

  getOperativaGroupHolidays(params?: {
    operativa_group_id?: number;
    exercise_id?: number;
  }): Observable<OperativaGroupHoliday[]> {
    let path = '/holidays/operativa-group-holidays/';

    const queryParams: string[] = [];
    if (params?.operativa_group_id !== undefined) {
      queryParams.push(`operativa_group_id=${params.operativa_group_id}`);
    }
    if (params?.exercise_id !== undefined) {
      queryParams.push(`exercise_id=${params.exercise_id}`);
    }

    if (queryParams.length > 0) {
      path = `/holidays/operativa-group-holidays/?${queryParams.join('&')}`;
    }

    return this.apiService.buildAPIGet<OperativaGroupHoliday[]>(path);
  }

  getOperativaGroupHolidayById(id: number): Observable<OperativaGroupHoliday> {
    const path = `/holidays/operativa-group-holidays/${id}/`;
    return this.apiService.buildAPIGet<OperativaGroupHoliday>(path);
  }

  createOperativaGroupHoliday(
    data: OperativaGroupHolidayRequest
  ): Observable<OperativaGroupHoliday> {
    const path = '/holidays/operativa-group-holidays/';
    return this.apiService.buildAPIPost<OperativaGroupHoliday>(path, data);
  }

  updateOperativaGroupHoliday(
    id: number,
    data: OperativaGroupHolidayRequest
  ): Observable<OperativaGroupHoliday> {
    const path = '/holidays/operativa-group-holidays/';
    return this.apiService.buildAPIPut<OperativaGroupHoliday>(path, id, data);
  }

  deleteOperativaGroupHoliday(id: number): Observable<any> {
    const path = '/holidays/operativa-group-holidays/';
    return this.apiService.buildAPIDelete(path, id);
  }

  // Get all operativas in a group with their assignment status for a specific exercise
  getOperativaGroupOperativas(params: {
    operativa_group_id: number;
    exercise_id: number;
  }): Observable<OperativaGroupOperativasResponse> {
    const path = `/holidays/operativa-groups/operativas/?operativa_group_id=${params.operativa_group_id}&exercise_id=${params.exercise_id}`;
    return this.apiService.buildAPIGet<OperativaGroupOperativasResponse>(path);
  }

  // Create or update an operativa assignment (upsert)
  upsertOperativaAssignment(
    data: UpsertOperativaAssignmentRequest
  ): Observable<UpsertOperativaAssignmentResponse> {
    const path = '/holidays/operativa-assignments/upsert';
    return this.apiService.buildAPIPut<UpsertOperativaAssignmentResponse>(
      path,
      '',
      data
    );
  }

  deleteProtocol(id: number): Observable<void> {
    const path = '/holidays/protocols/';
    return this.apiService.buildAPIDelete<void>(path, id);
  }

  downloadOperativaAssignments(data: {
    operativa_group_id: number;
    exercise_id: number;
    week_numbers: number[];
  }): Observable<DownloadOperativaAssignmentsResponse> {
    const path = '/holidays/operativa-assignments/download/';
    return this.apiService.buildAPIPost<DownloadOperativaAssignmentsResponse>(path, data);
  }

  syncXancletesNightly(): Observable<any> {
    const path = '/holidays/xancletes/sync/admin/';
    return this.apiService.buildAPIPost<any>(path);
  }

  createHolidayRequest(
    requestData: CreateHolidayRequestPayload
  ): Observable<CreateHolidayRequestResponse> {
    return this.apiService.buildAPIPost<CreateHolidayRequestResponse>(
      '/holidays/vacation-requests/',
      requestData
    );
  }

  UnavailabilityEmployee(driver_id: number): Observable<any> {
    const path = '/holidays/unavailability/';
    const body = { driverid: driver_id };
    return this.apiService.buildAPIPost<any>(path, body);
  }

  FixSeniorityEntitlements(): Observable<any> {
    const path = '/holidays/fix-seniority-entitlements/?dry_run=false';
    return this.apiService.buildAPIPost<any>(path);
  }

  getXancletesJobStatus(statusUrl: string): Observable<any> {
    return this.apiService.buildAPIGet<any>(statusUrl);
  }
}
